import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import SearchableSelect from '../ui/SearchableSelect';
import { Checkbox } from '../ui/checkbox';
import { X, Star } from 'lucide-react';
import api from '../../services/api';
import { debounce } from 'lodash';
import {
  getChoiceLayoutClass, resolveOptionValue, sanitizeTextInput, wordCount,
} from '../../utils/fieldProperties';
import { computeFieldStates } from '../../utils/fieldRules';
import { validateFieldValue } from '../../utils/fieldValidation';
import { RichTextView } from '../ui/RichTextInput';
import { hexToRgba } from '../../utils/colorUtils';

const DEFAULT_THEME = {
  primary_color: '#7c3aed',
  secondary_color: '#14b8a6',
  background_color: '#ffffff',
  text_color: '#1e293b',
  accent_color: '#7c3aed',
  font_family: 'Plus Jakarta Sans',
  card_style: 'solid',
  gradient: null,
  page_background: '#f8fafc'
};

// custom_amount contribution to the total: empty/non-numeric input counts as 0,
// otherwise the typed value as-is — NOT clamped to min_value/max_value. Mirrors the
// public form (PublicRegistration/PublicLandingPage) so the preview total matches
// what registrants and the server compute; see safeCustomAmount there for why
// clamping here would show the admin a misleading preview total.
const safeCustomAmount = (raw) => {
  if (raw === undefined || raw === null || raw === '') return 0;
  const amount = parseFloat(raw);
  return isNaN(amount) ? 0 : amount;
};

const FormPreview = ({ 
  fields, 
  basePrice, 
  campaignName, 
  onClose, 
  fieldRules = [], 
  pricingRules = [], 
  pricingFormula = '', 
  campaignId = null,
  theme = null,
  headerImage = null,
  submitButtonText = ''
}) => {
  const [formData, setFormData] = useState({});
  const [calculatedPrice, setCalculatedPrice] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [validationRun, setValidationRun] = useState(false);

  // Merge default theme with provided theme
  const activeTheme = { ...DEFAULT_THEME, ...theme };

  // Field rules are evaluated locally (same semantics as the public form) —
  // synchronous, no server round-trip.
  const fieldStates = useMemo(
    () => computeFieldStates(fieldRules, formData, fields),
    [fieldRules, formData, fields]
  );

  // Calculate price when form data changes
  const calculatePrice = useCallback(
    debounce(async (values) => {
      if (!campaignId) {
        // Local calculation
        let total = parseFloat(basePrice) || 0;
        fields.forEach((field) => {
          // custom_amount adds the registrant-entered value (clamped to min/max),
          // matching the public form's local price computation.
          if (field.type === 'custom_amount') {
            total += safeCustomAmount(values[field.id]);
            return;
          }
          if (field.options && values[field.id]) {
            const selectedValues = Array.isArray(values[field.id])
              ? values[field.id]
              : [values[field.id]];

            field.options.forEach((option) => {
              if (selectedValues.includes(option.value)) {
                total += parseFloat(option.price_modifier) || 0;
              }
            });
          }
        });
        setCalculatedPrice({ total, base_price: basePrice, subtotal: total });
        return;
      }

      try {
        const response = await api.post(`/campaigns/${campaignId}/calculate-price`, {
          base_price: parseFloat(basePrice) || 0,
          field_values: values,
          pricing_rules: pricingRules,
          pricing_formula: pricingFormula
        });
        // .NET /calculate-price serializes the total as `total` (snake_case),
        // not `total_price` — normalize so calculateTotal() reads a real number.
        setCalculatedPrice({ ...response.data, total: response.data.total });
      } catch (error) {
        console.error('Failed to calculate price:', error);
      }
    }, 300),
    [campaignId, basePrice, pricingRules, pricingFormula, fields]
  );

  // Calculate price when form data changes
  useEffect(() => {
    calculatePrice(formData);
  }, [formData, calculatePrice]);

  const handleChange = (fieldId, value) => {
    setFormData((prev) => ({ ...prev, [fieldId]: value }));
    const baseId = fieldId.replace(/_(first|middle|last|country)$/, '');
    setFieldErrors((prev) => {
      if (!prev[baseId]) return prev;
      const next = { ...prev };
      delete next[baseId];
      return next;
    });
  };

  // Runs the public form's validator over the preview's data so the admin can see
  // exactly which rules fire and with what wording, without submitting anything.
  const runValidation = () => {
    const errors = {};
    fields.forEach((field) => {
      if (!isFieldVisible(field.id)) return;
      const message = validateFieldValue(field, formData, { required: isFieldRequired(field) });
      if (message) errors[field.id] = message;
    });
    setFieldErrors(errors);
    setValidationRun(true);
  };

  const calculateTotal = () => {
    if (calculatedPrice) {
      // Guard against a missing total so the render's .toFixed(2) can never throw.
      return calculatedPrice.total ?? 0;
    }

    let total = parseFloat(basePrice) || 0;

    fields.forEach((field) => {
      // custom_amount adds the registrant-entered value as typed (see safeCustomAmount).
      if (field.type === 'custom_amount') {
        total += safeCustomAmount(formData[field.id]);
        return;
      }
      if (field.options && formData[field.id]) {
        const selectedValues = Array.isArray(formData[field.id])
          ? formData[field.id]
          : [formData[field.id]];

        field.options.forEach((option) => {
          if (selectedValues.includes(option.value)) {
            total += parseFloat(option.price_modifier) || 0;
          }
        });
      }
    });

    return total;
  };

  // Check if a field should be visible based on field rules
  const isFieldVisible = (fieldId) => {
    return fieldStates[fieldId]?.visible !== false;
  };

  // Check if a field is required (either by default or by field rules)
  const isFieldRequired = (field) => {
    return field.required || fieldStates[field.id]?.required === true;
  };

  // Renders a field and appends its validation error inside the same wrapper, so
  // every field type shows the message without repeating markup in each branch.
  const renderField = (field) => {
    const rendered = renderFieldControl(field);
    if (!rendered) return rendered;
    const message = fieldErrors[field.id];

    // Cloned on EVERY render, not only when there's a message: React.Children.toArray
    // re-keys the children, so cloning conditionally would change the key scheme the
    // moment an error appears or clears — remounting the input and stealing focus from
    // the registrant mid-keystroke. Keeping the shape constant keeps focus.
    return React.cloneElement(
      rendered,
      { key: field.id },
      ...React.Children.toArray(rendered.props.children),
      message ? (
        <p key="__validation_error" className="text-xs text-red-600 font-jakarta mt-1" role="alert">
          {message}
        </p>
      ) : null
    );
  };

  const renderFieldControl = (field) => {
    // Check visibility
    if (!isFieldVisible(field.id)) {
      return null;
    }

    // New width names (small/medium/large) + legacy (full/half/third) for back-compat.
    const widthClass = {
      // New
      small:  'col-span-6',
      medium: 'col-span-8',
      large:  'col-span-12',
      // Legacy (forms saved before the rename keep rendering correctly)
      full:   'col-span-12',
      half:   'col-span-6',
      third:  'col-span-4',
    }[field.width] || 'col-span-12';

    switch (field.type) {
      case 'name':
        const nameConfig = field.name_config || {};
        const showMiddle = nameConfig.show_middle_name;
        const showLast = nameConfig.show_last_name !== false;
        
        // Calculate grid columns based on visible fields
        let gridCols = 1;
        if (showMiddle && showLast) gridCols = 3;
        else if (showLast || showMiddle) gridCols = 2;
        
        return (
          <div key={field.id} className={widthClass}>
            <Label className="text-slate-700 font-jakarta font-medium mb-2 block">
              {field.label} {isFieldRequired(field) && <span className="text-accent">*</span>}
            </Label>
            <div className={`grid grid-cols-${gridCols} gap-3`} style={{ 
              display: 'grid', 
              gridTemplateColumns: `repeat(${gridCols}, 1fr)`,
              gap: '0.75rem'
            }}>
              {/* First Name - Always shown */}
              <div>
                <Label className="text-slate-500 font-jakarta text-xs mb-1 block">
                  {nameConfig.first_name_label || 'First Name'}
                </Label>
                <Input
                  type="text"
                  value={formData[`${field.id}_first`] || ''}
                  onChange={(e) => handleChange(`${field.id}_first`, sanitizeTextInput(e.target.value, field))}
                  maxLength={field.max_length || undefined}
                  placeholder={nameConfig.first_name_placeholder || 'Enter first name'}
                  required={isFieldRequired(field)}
                  className="h-11 rounded-lg border-slate-200 focus-visible:ring-primary"
                />
              </div>
              
              {/* Middle Name - Optional */}
              {showMiddle && (
                <div>
                  <Label className="text-slate-500 font-jakarta text-xs mb-1 block">
                    {nameConfig.middle_name_label || 'Middle Name'}
                  </Label>
                  <Input
                    type="text"
                    value={formData[`${field.id}_middle`] || ''}
                    onChange={(e) => handleChange(`${field.id}_middle`, sanitizeTextInput(e.target.value, field))}
                    maxLength={field.max_length || undefined}
                    placeholder={nameConfig.middle_name_placeholder || 'Enter middle name'}
                    className="h-11 rounded-lg border-slate-200 focus-visible:ring-primary"
                  />
                </div>
              )}
              
              {/* Last Name - Optional but recommended */}
              {showLast && (
                <div>
                  <Label className="text-slate-500 font-jakarta text-xs mb-1 block">
                    {nameConfig.last_name_label || 'Last Name'}
                  </Label>
                  <Input
                    type="text"
                    value={formData[`${field.id}_last`] || ''}
                    onChange={(e) => handleChange(`${field.id}_last`, sanitizeTextInput(e.target.value, field))}
                    maxLength={field.max_length || undefined}
                    placeholder={nameConfig.last_name_placeholder || 'Enter last name'}
                    required={field.required}
                    className="h-11 rounded-lg border-slate-200 focus-visible:ring-primary"
                  />
                </div>
              )}
            </div>
            {field.help_text && <p className="text-xs text-slate-500 font-jakarta mt-2">{field.help_text}</p>}
          </div>
        );

      case 'section_header':
        return (
          <div key={field.id} className="col-span-12 mb-4">
            <h3 className="text-xl font-outfit font-semibold text-slate-900 mb-2">{field.label}</h3>
            {field.help_text && <p className="text-sm text-slate-600 font-jakarta">{field.help_text}</p>}
          </div>
        );

      case 'dropdown':
        return (
          <div key={field.id} className={widthClass}>
            <Label className="text-slate-700 font-jakarta font-medium mb-2 block">
              {field.label} {field.required && <span className="text-accent">*</span>}
            </Label>
            <SearchableSelect
              id={field.id}
              value={formData[field.id] || ''}
              onChange={(v) => handleChange(field.id, v)}
              options={(field.options || []).map((opt) => ({
                value: resolveOptionValue(opt),
                label: opt.price_modifier > 0 ? `${opt.label} (+₹${opt.price_modifier})` : opt.label,
              }))}
              required={field.required}
              className="w-full h-11 rounded-lg border border-slate-200 pl-3 pr-10 py-2 text-sm flex items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            />
            {field.help_text && <p className="text-xs text-slate-500 font-jakarta mt-1">{field.help_text}</p>}
          </div>
        );

      case 'radio':
        return (
          <div key={field.id} className={widthClass}>
            <Label className="text-slate-700 font-jakarta font-medium mb-2 block">
              {field.label} {field.required && <span className="text-accent">*</span>}
            </Label>
            <div className={getChoiceLayoutClass(field.column_layout)}>
              {field.options?.map((opt) => {
                const submitValue = resolveOptionValue(opt);
                return (
                  <label key={opt.value ?? opt.label} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name={field.id}
                      value={submitValue}
                      checked={formData[field.id] === submitValue}
                      onChange={(e) => handleChange(field.id, e.target.value)}
                      required={field.required}
                      className="w-4 h-4 text-primary"
                    />
                    <span className="text-sm font-jakarta text-slate-700">
                      {opt.label} {opt.price_modifier > 0 && `(+₹${opt.price_modifier})`}
                    </span>
                  </label>
                );
              })}
            </div>
            {field.help_text && <p className="text-xs text-slate-500 font-jakarta mt-1">{field.help_text}</p>}
          </div>
        );

      case 'checkbox':
        if (!field.options || field.options.length === 0) {
          return (
            <div key={field.id} className={widthClass}>
              <Label className="text-slate-700 font-jakarta font-medium mb-2 block">
                {field.label} {field.required && <span className="text-accent">*</span>}
              </Label>
              <p className="text-sm text-amber-600 bg-amber-50 p-2 rounded-lg">
                ⚠️ No options configured for this checkbox field
              </p>
            </div>
          );
        }
        {
          const selected = formData[field.id] || [];
          const maxSel   = field.max_selections;
          const atMax    = maxSel && selected.length >= maxSel;
          return (
            <div key={field.id} className={widthClass}>
              <Label className="text-slate-700 font-jakarta font-medium mb-2 block">
                {field.label} {field.required && <span className="text-accent">*</span>}
                {maxSel && (
                  <span className={`ml-2 text-xs font-normal ${atMax ? 'text-amber-600' : 'text-slate-500'}`}>
                    ({selected.length}/{maxSel} selected)
                  </span>
                )}
              </Label>
              <div className={getChoiceLayoutClass(field.column_layout)}>
                {field.options.map((opt) => {
                  const submitValue = resolveOptionValue(opt);
                  const isChecked   = selected.includes(submitValue);
                  const lockedOut   = atMax && !isChecked;
                  return (
                    <label key={opt.value ?? opt.label} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        value={submitValue}
                        checked={isChecked}
                        disabled={lockedOut}
                        onChange={(e) => {
                          const current = formData[field.id] || [];
                          const updated = e.target.checked
                            ? [...current, submitValue]
                            : current.filter((v) => v !== submitValue);
                          handleChange(field.id, updated);
                        }}
                        className="w-4 h-4 text-primary rounded"
                      />
                      <span className={`text-sm font-jakarta ${lockedOut ? 'text-slate-400' : 'text-slate-700'}`}>
                        {opt.label} {opt.price_modifier > 0 && `(+₹${opt.price_modifier})`}
                      </span>
                    </label>
                  );
                })}
              </div>
              {field.help_text && <p className="text-xs text-slate-500 font-jakarta mt-1">{field.help_text}</p>}
            </div>
          );
        }

      case 'terms_checkbox':
        return (
          <div key={field.id} className="col-span-12">
            <label className="flex items-start space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData[field.id] || false}
                onChange={(e) => handleChange(field.id, e.target.checked)}
                required={field.required}
                className="w-4 h-4 text-primary rounded mt-1"
              />
              <span className="text-sm font-jakarta text-slate-700">
                {field.label} {field.required && <span className="text-accent">*</span>}
                {field.terms_url && (
                  <a
                    href={field.terms_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    // Don't let the click bubble to the label and toggle the checkbox
                    onClick={(e) => e.stopPropagation()}
                    className="ml-2 text-primary underline hover:text-primary-hover"
                  >
                    View terms
                  </a>
                )}
              </span>
            </label>
            {field.help_text && <RichTextView html={field.help_text} className="text-sm mt-1 ml-6" />}
          </div>
        );

      case 'currency':
      case 'custom_amount':
        return (
          <div key={field.id} className={widthClass}>
            <Label className="text-slate-700 font-jakarta font-medium mb-2 block">
              {field.label} {field.required && <span className="text-accent">*</span>}
            </Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">₹</span>
              <Input
                type="number"
                min={field.min_value ?? undefined}
                max={field.max_value ?? undefined}
                value={formData[field.id] || ''}
                onChange={(e) => handleChange(field.id, e.target.value)}
                placeholder={field.placeholder || '0'}
                required={field.required}
                className="h-11 rounded-lg border-slate-200 focus-visible:ring-primary pl-7"
              />
            </div>
            {field.help_text && <p className="text-xs text-slate-500 font-jakarta mt-1">{field.help_text}</p>}
          </div>
        );

      case 'rating': {
        const maxRating = Math.max(3, Math.min(10, parseInt(field.max_rating, 10) || 5));
        const currentRating = parseInt(formData[field.id], 10) || 0;
        return (
          <div key={field.id} className={widthClass}>
            <Label className="text-slate-700 font-jakarta font-medium mb-2 block">
              {field.label} {field.required && <span className="text-accent">*</span>}
            </Label>
            <div className="flex items-center gap-1">
              {Array.from({ length: maxRating }, (_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => handleChange(field.id, i + 1)}
                  className="p-0.5 transition-transform hover:scale-110"
                  aria-label={`Rate ${i + 1} of ${maxRating}`}
                >
                  <Star
                    className={`w-6 h-6 ${i < currentRating ? 'text-amber-400 fill-amber-400' : 'text-slate-300'}`}
                  />
                </button>
              ))}
            </div>
            {field.help_text && <p className="text-xs text-slate-500 font-jakarta mt-1">{field.help_text}</p>}
          </div>
        );
      }

      case 'textarea': {
        const txt = formData[field.id] || '';
        const wc  = wordCount(txt);
        return (
          <div key={field.id} className={widthClass}>
            <Label className="text-slate-700 font-jakarta font-medium mb-2 block">
              {field.label} {field.required && <span className="text-accent">*</span>}
            </Label>
            <textarea
              value={txt}
              onChange={(e) => handleChange(field.id, sanitizeTextInput(e.target.value, field))}
              placeholder={field.placeholder}
              required={field.required}
              rows={4}
              maxLength={field.max_length || undefined}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            />
            {(field.max_length || field.max_words) && (
              <p className="text-xs text-slate-400 font-jakarta mt-1 text-right space-x-2">
                {field.max_length && <span>{txt.length} / {field.max_length} chars</span>}
                {field.max_words && (
                  <span className={wc > field.max_words ? 'text-red-500' : ''}>
                    {wc} / {field.max_words} words
                  </span>
                )}
              </p>
            )}
            {field.help_text && <p className="text-xs text-slate-500 font-jakarta mt-1">{field.help_text}</p>}
          </div>
        );
      }

      case 'rich_text':
        return (
          <div key={field.id} className={widthClass}>
            {field.label && (
              <Label className="text-slate-700 font-jakarta font-medium mb-2 block">
                {field.label}
              </Label>
            )}
            <RichTextView
              html={field.content || ''}
              bgColor={field.content_bg_color}
              borderColor={field.content_border_color}
            />
            {field.help_text && <p className="text-xs text-slate-500 font-jakarta mt-1">{field.help_text}</p>}
          </div>
        );

      default:
        return (
          <div key={field.id} className={widthClass}>
            <Label className="text-slate-700 font-jakarta font-medium mb-2 block">
              {field.label} {field.required && <span className="text-accent">*</span>}
            </Label>
            <Input
              type={field.type}
              value={formData[field.id] || ''}
              onChange={(e) => handleChange(field.id, sanitizeTextInput(e.target.value, field))}
              placeholder={field.placeholder}
              required={field.required}
              maxLength={field.max_length || undefined}
              className="h-11 rounded-lg border-slate-200 focus-visible:ring-primary"
            />
            {field.max_length && (
              <p className="text-xs text-slate-400 font-jakarta mt-1 text-right">
                {(formData[field.id] || '').length} / {field.max_length}
              </p>
            )}
            {field.help_text && <p className="text-xs text-slate-500 font-jakarta mt-1">{field.help_text}</p>}
          </div>
        );
    }
  };

  return (
    <div
      className="min-h-screen p-8"
      style={{
        background: activeTheme.page_background || activeTheme.gradient || '#f8fafc',
        backgroundImage: activeTheme.page_background_image ? `url(${activeTheme.page_background_image})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        fontFamily: activeTheme.font_family || 'Plus Jakarta Sans'
      }}
    >
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-outfit font-bold mb-2" style={{ color: activeTheme.text_color }}>
              Form Preview
            </h1>
            <p style={{ color: activeTheme.text_color, opacity: 0.7 }}>{campaignName}</p>
          </div>
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="close-preview"
            className="border-slate-200 hover:bg-slate-50 font-jakarta rounded-lg"
          >
            <X className="w-4 h-4 mr-2" />
            Close Preview
          </Button>
        </div>

        {/* Header Image */}
        {headerImage && (
          <div className="mb-6 rounded-xl overflow-hidden shadow-lg">
            <img 
              src={headerImage} 
              alt="Form header" 
              className="w-full h-48 object-cover"
            />
          </div>
        )}

        {/* Form Card */}
        <Card
          className="rounded-xl border shadow-card p-8"
          style={{
            backgroundColor: activeTheme.background_color,
            backgroundImage: activeTheme.background_image
              ? `linear-gradient(${hexToRgba(activeTheme.background_color, 0.85)}, ${hexToRgba(activeTheme.background_color, 0.85)}), url(${activeTheme.background_image})`
              : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            borderColor: activeTheme.card_style === 'glass' ? 'rgba(255,255,255,0.2)' : '#e2e8f0',
            backdropFilter: activeTheme.card_style === 'glass' ? 'blur(10px)' : 'none'
          }}
        >
          <form className="grid grid-cols-12 gap-6">
            {fields.map((field) => renderField(field))}
          </form>

          {/* Total Price */}
          {basePrice > 0 && (
            <div className="mt-8 pt-6 border-t" style={{ borderColor: `${activeTheme.text_color}20` }}>
              <div className="flex items-center justify-between">
                <span className="text-lg font-outfit font-semibold" style={{ color: activeTheme.text_color }}>
                  Total Amount
                </span>
                <span className="text-2xl font-outfit font-bold" style={{ color: activeTheme.primary_color }}>
                  ₹{calculateTotal().toFixed(2)}
                </span>
              </div>
            </div>
          )}

          {/* Submit Button (Preview Only).
              Clicking runs the same validation the public form runs — so the admin can
              check their limits and their custom messages here — but never submits. */}
          <div className="mt-6">
            <Button
              type="button"
              onClick={runValidation}
              className="w-full font-jakarta font-medium rounded-lg h-12"
              style={{
                backgroundColor: activeTheme.primary_color,
                color: '#ffffff'
              }}
            >
              {submitButtonText || 'Register & Pay'}
            </Button>
            {validationRun && !Object.keys(fieldErrors).length && (
              <p className="text-xs text-center mt-2 text-emerald-600 font-jakarta">
                All validation rules pass.
              </p>
            )}
            <p className="text-xs text-center mt-2" style={{ color: activeTheme.text_color, opacity: 0.5 }}>
              This is a preview — the button checks your validation rules and messages, nothing is submitted.
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default FormPreview;
